import { Token, User } from 'hooks/use-extension-auth';
import _ from 'lodash';
import Logger from '../logger';
import { API_V1 } from './APIs/APIv1';

interface LoginInterface {
  email: string;
  password: string;
}

interface LoginResponse {
  data?: {
    token: Token;
    user: User;
  };
  error?: any;
}

interface RegisterInterface {
  firstName: string;
  lastName: string;
  email: string;
  interest?: string;
  password: string;
  companyName?: string;
  language: string;
  /** Server rejects the registration without this — see auth.service register(). */
  acceptedLegalTerms: true;
  /** Ad/website origin from the sign-up URL (whitelisted keys only, see features/engagement/signup). */
  attribution?: Record<string, string>;
  /** Optional, unticked by default: offers and tips by e-mail (document id `marketing-email`). */
  consentMarketingEmail?: boolean;
  /** Optional, unticked by default: purchase signal to Google/Meta (document id `ad-measurement`). */
  consentAdMeasurement?: boolean;
}

export const login = async (input: LoginInterface): Promise<LoginResponse> => {
  try {
    const response = await API_V1.post('/auth/login', input, {
      withCredentials: true,
    });

    return {
      data: {
        token: {
          accessToken: response.data.accessToken,
          refreshToken: response.data.refreshToken,
          expiresIn: response.data.expiresIn,
        },
        user: response.data.user,
      },
    };
  } catch (e) {
    Logger.trace('Error while logging in user with email %s', input.email);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const sudoLogin = async (
  userEmail: string,
  adminEmail: string,
  password: string,
  tenantId?: string
): Promise<LoginResponse> => {
  try {
    const response = await API_V1.post(
      '/auth/sudo-login',
      { userEmail, adminEmail, password, tenantId },
      {
        withCredentials: true,
      }
    );

    return {
      data: {
        token: {
          accessToken: response.data.accessToken,
          refreshToken: response.data.refreshToken,
          expiresIn: response.data.expiresIn,
        },
        user: response.data.user,
      },
    };
  } catch (e) {
    Logger.trace('Error while logging in user with email %s', userEmail);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

/**
 * Ends an impersonation session and returns the admin's own session.
 * The current (impersonated) token carries the signed `sudoAdmin` claim the
 * server needs, so no password is required.
 */
export const exitSudo = async (refreshToken?: string): Promise<LoginResponse> => {
  try {
    const response = await API_V1.post(
      '/auth/exit-sudo',
      { refreshToken },
      { withCredentials: true }
    );

    return {
      data: {
        token: {
          accessToken: response.data.accessToken,
          refreshToken: response.data.refreshToken,
          expiresIn: response.data.expiresIn,
        },
        user: response.data.user,
      },
    };
  } catch (e) {
    Logger.trace('Error while returning from impersonation to the admin account');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const logout = async () => {
  try {
    const response = await API_V1.post('/auth/logout', {});
    localStorage.removeItem('token');
    return response.data;
  } catch (e) {
    Logger.trace('Error while logging out user');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const register = async (input: RegisterInterface) => {
  try {
    const extraInput = {
      ...input,
    };
    const response = await API_V1.post('/auth/register', extraInput);
    return response.data;
  } catch (e) {
    Logger.trace('Error while registering new user');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const getProducts = async () => {
  try {
    const response = await API_V1.get('/licenses/products');
    return response.data;
  } catch (e) {
    Logger.trace('Error while getting account products');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};
